import * as React from "react";
import { Card } from "antd";
import { CardContent } from "../../../components/antd/card/CardContent";
import { CardTitle } from "../../../components/antd/card/CardTitle";
import { ExternalLink } from "../../../newcomponents/table/components/ExternalLink";
import { DOCS_URL } from "../../../envVariables";
import { CaretRightOutlined } from "@ant-design/icons";
import { v4 as uuidv4 } from "uuid";
import { formSetValue, useCpFormValue } from "../../gvc/testNewForm/utils";
import { store } from "../../../store";
import { IdentityInput } from "../../../schema/types/identity/input";
import { useCpFormContext } from "../../../components/form/useCpFormContext";

const STORAGE_KEY = "identity-overview-card-seen";

export const Overview: React.FC = () => {
  const { id } = useCpFormContext();
  const collapsedCards = useCpFormValue(`meta.collapsedCards`);
  const isCollapsed = collapsedCards.some((i: { value: string }) => i.value === `overview`);

  // On first visit, show expanded. After that, default to collapsed.
  React.useEffect(() => {
    const hasSeenBefore = localStorage.getItem(STORAGE_KEY) === "true";
    if (!hasSeenBefore) {
      // First time - mark as seen for next time
      localStorage.setItem(STORAGE_KEY, "true");
    } else {
      // User has seen before - collapse it by default
      const form = store.getState().form.instances[id];
      const values: IdentityInput = form.values as any;
      const alreadyCollapsed = values.meta.collapsedCards.some((i: { value: string }) => i.value === `overview`);
      if (!alreadyCollapsed) {
        const currentCollapsibleCards = [...values.meta.collapsedCards];
        currentCollapsibleCards.push({ id: uuidv4(), value: `overview` });
        formSetValue(id, `meta.collapsedCards`, currentCollapsibleCards);
      }
    }
  }, []);

  return (
    <>
      <div className="cpln-form-section cpln-ant-card-container">
        <Card>
          <CardTitle style={{ marginBottom: isCollapsed ? 0 : undefined }}>
            <div
              className={`flex items-baseline gap-2 color-link-hover cursor-pointer`}
              onClick={() => {
                const form = store.getState().form.instances[id];
                const values: IdentityInput = form.values as any;

                let currentCollapsibleCards = [...values.meta.collapsedCards];
                const isCollapsedItem = currentCollapsibleCards.find((i: { value: string }) => i.value === `overview`);
                if (isCollapsedItem) {
                  currentCollapsibleCards = currentCollapsibleCards.filter((i: { id: string }) => i.id !== isCollapsedItem.id);
                } else {
                  currentCollapsibleCards.push({ id: uuidv4(), value: `overview` });
                }
                formSetValue(id, `meta.collapsedCards`, currentCollapsibleCards);
              }}
            >
              <CaretRightOutlined rotate={isCollapsed ? 0 : 90} className={`self-center`} />
              <div>What is Identity?</div>
            </div>
          </CardTitle>
          <CardContent collapsed={isCollapsed}>
            <div>
              <div className="mb-4">
                In Control Plane, an <span className="font-semibold">identity</span> defines{" "}
                <span className="font-semibold">what a workload is allowed to do</span>. It is a named set of
                capabilities -like permissions and network access— that you can assign to one or more workloads.
              </div>
              <div className="mb-4">
                Think of it like a <span className="font-semibold">service account</span> in other systems, but more
                powerful and cloud-native.
              </div>
              <div className="mb-2">When you assign an identity to a workload, that workload can:</div>
              <ul className="pl-6">
                <li style={{ listStyleType: "disc" }}>
                  <span className="font-semibold">Access cloud resources</span> securely across{" "}
                  <span className="font-semibold">one or more cloud accounts</span> (e.g., read from an S3 bucket in AWS
                  and write to a GCS bucket in GCP)
                </li>
                <li style={{ listStyleType: "disc" }}>
                  <span className="font-semibold">Establish secure network tunnels</span> between workloads across{" "}
                  <span className="font-semibold">clouds or on-prem</span> environments (via{" "}
                  <span className="font-semibold">Cloud Wormholes</span>)
                </li>
                <li style={{ listStyleType: "disc" }}>
                  <span className="font-semibold">Use native cloud networking</span> features like{" "}
                  <span className="font-semibold">AWS PrivateLink</span> or{" "}
                  <span className="font-semibold">GCP Private Service Connect</span> (ideal for single-cloud use cases)
                </li>
                <li style={{ listStyleType: "disc" }}>
                  <span className="font-semibold">Call Control Plane APIs and CLI tools</span> The identity acts as a{" "}
                  <span className="font-semibold">valid principal</span> in Control Plane policies. You can define what
                  actions a workload can perform on your platform—whether it's creating resources, reading secrets, or
                  managing other workloads.
                </li>
              </ul>
              <div className="mt-2">
                <ExternalLink
                  label="Click here to read more in documentation"
                  url={`${DOCS_URL}/reference/identity`}
                  disableCopy
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
};
